import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import * as authActions from 'global/actions/authActions';
import * as resetActions from 'global/actions/passwordActions';
import { useActions } from 'hooks/useActions';
import { Input } from 'user/components';
import { Header, Body, Footer, FooterButton } from 'components/Modals/modalElements';

const english = 'en';

// Get fragment with modal reset password functionality
export default function ModalContentResetPassword({ email, setEmail, onCancel, goToLogin }) {
  const { initialize, clearAuthState } = useActions(authActions);

  const {
    loading: resetLoading,
    error: resetError,
    success: resetSuccess,
  } = useSelector((state) => state.managePassword);
  const { clearError: resetClearError, clearState: resetClearState, resetPassword } = useActions(resetActions);

  useEffect(() => {
    if (resetSuccess) {
      let interval = setInterval(() => initialize(true), 1000);
      return () => clearInterval(interval);
    }
  }, [resetSuccess, initialize]);

  useEffect(() => {
    if (resetError) {
      toast.error(resetError?.[english] || resetError);
      resetClearError();
    }
  }, [resetError, resetClearError]);

  useEffect(() => {
    resetClearState();
  }, [resetClearState]);

  const performResetPassword = () => {
    clearAuthState();
    resetPassword(email);
  };

  const onEnterResetPassword = (e) => e.key === 'Enter' && email && !resetLoading && performResetPassword();

  const resetPasswordModalBody = () => (
    <>
      <Header>Reset password</Header>
      <Body loading={resetLoading}>
        <Input
          type="text"
          autoFocus
          value={email}
          placeholder="Email"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onEnterResetPassword}
        />
        <p>An email will be sent to you with a link that you can use to set a new password.</p>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Cancel" />
        <FooterButton onClick={goToLogin} label="Log In" />
        <FooterButton onClick={performResetPassword} label="Send Email" disabled={resetLoading || !email} />
      </Footer>
    </>
  );

  const resetPasswordSuccessBody = () => (
    <>
      <Header>Success</Header>
      <Body>
        <p>Your password has been reset. We have sent you an email with a link to set a new password.</p>
        <p>Leave this modal open to automatically sign in when a new password has been set.</p>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Close" />
      </Footer>
    </>
  );

  return !resetSuccess ? resetPasswordModalBody() : resetPasswordSuccessBody();
}
